import axios from "axios";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

const api = axios.create({
  baseURL: API_URL,
  timeout: 300000,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

const clearSessionAndRedirect = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("token_expires_at");
  localStorage.removeItem("activeBranches");
  window.location.href = "/";
};

const refreshAccessToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const currentToken = localStorage.getItem("token");

    if (!currentToken) {
      throw new Error("No hay token disponible para refresh");
    }

    const response = await axios.post(
      `${API_URL}/refresh`,
      {},
      {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      },
    );

    const newToken = response.data.token;
    const expiresAt = response.data.expires_at;

    if (!newToken) {
      throw new Error("El refresh no devolvió token");
    }

    localStorage.setItem("token", newToken);
    if (expiresAt) {
      localStorage.setItem("token_expires_at", expiresAt);
    }
    api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
    processQueue(null, newToken);

    return newToken;
  } catch (refreshError) {
    processQueue(refreshError, null);
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
};

// Interceptor request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const branches = localStorage.getItem("activeBranches");

    if (branches) {
      config.headers["X-Active-Branches"] = branches;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor response
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || "";

    const isLoginRequest =
      requestUrl.includes("/login") || requestUrl.endsWith("login");

    const isRefreshRequest =
      requestUrl.includes("/refresh") || requestUrl.endsWith("refresh");

    if (
      status === 401 &&
      !isLoginRequest &&
      !isRefreshRequest &&
      !originalRequest._retry
    ) {
      const expiresAt = localStorage.getItem("token_expires_at");
      if (expiresAt && new Date() >= new Date(expiresAt)) {
        clearSessionAndRedirect();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        clearSessionAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// Helpers
const requestGet = (url, params = {}) => {
  return api.get(url, { params });
};

const requestPost = (url, data = {}) => {
  return api.post(url, data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

const requestPut = (url, data = {}) => {
  return api.put(url, data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

const requestPatch = (url, data = {}) => {
  return api.patch(url, data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

const requestPutFile = (url, data = {}) => {
  if (data instanceof FormData) {
    data.append("_method", "PUT");
  }

  return api.post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const requestDelete = (url, data = {}) => {
  return api.delete(url, { data });
};

const requestPostFile = (url, data = {}) => {
  return api.post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const requestPostFileBlob = (url, data = {}) => {
  return api.post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    responseType: "blob",
  });
};

export {
  requestGet,
  requestPost,
  requestPut,
  requestPatch,
  requestPutFile,
  requestDelete,
  requestPostFile,
  requestPostFileBlob,
  api,
  refreshAccessToken,
  API_URL,
};
